import { concatMap, debounceTime, finalize, tap } from 'rxjs/operators';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TripService } from '../shared/trip.service';
import { ActivatedRoute, Params } from '@angular/router';
import { Trip } from '../shared/trip.model';
import { Subject, Subscription } from 'rxjs';
import { AngularFireDatabase } from '@angular/fire/database';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from '../../shared/services/loader.service';
import { TripManagementService } from '../shared/trip-management-service';
import { DistanceUnit } from '../../shared/distance-unit';
import { MapManagementService } from '../shared/map-management-service';
import { LoadTrackingMapComponent } from '../load-tracking-monitoring/load-tracking-map/load-tracking-map.component';
import LatLngLiteral = google.maps.LatLngLiteral;
import Map = google.maps.Map;
import { DateTimezoneService } from '../shared/date-timezone.service';


@Component({
  selector: 'app-track-a-trip',
  templateUrl: './track-a-trip.component.html',
  styleUrls: ['./track-a-trip.component.scss']
})
export class TrackATripComponent implements OnInit, OnDestroy {

  trip: Trip;

  map: any;
  _showForecastPath = true;
  forecastPathReady = true;

  DistanceUnit = DistanceUnit;
  _distanceUnit = DistanceUnit.KILOMETERS;

  lat  = this.mapManagementService.defaultLat;
  lng  = this.mapManagementService.defaultLng;
  zoom = this.mapManagementService.defaultZoom;

  latLngLiteralSubject = new Subject<LatLngLiteral>();
  zoomSubject          = new Subject<number>();

  positionSubscription: Subscription;
  latLngLiteralSubscription: Subscription;
  zoomSubscription: Subscription
  refreshing = false;

  @ViewChild('loadTrackingMap', { static: true})
  loadTrackingMapComponent: LoadTrackingMapComponent;

  get showForecastPath(): boolean {
    return this._showForecastPath;
  }

  set showForecastPath(value: boolean) {
    this._showForecastPath = value;

    if (value) {
      this.changeShowForecastPath();
    }
  }

  get distanceUnit(): DistanceUnit {
    return this._distanceUnit;
  }
  set distanceUnit(value: DistanceUnit) {
    this._distanceUnit = value;

    if (value) {
      this.distanceUnitChanged(value);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private tripService: TripService,
    private tripManagementService: TripManagementService,
    private mapManagementService: MapManagementService,
    private dateTimezoneService: DateTimezoneService,
    private af: AngularFireDatabase,
    private toastr: ToastrService,
    private loader: LoaderService,
    private cdr: ChangeDetectorRef
  ) {
    route.params.subscribe((params: Params) => {
      const tripId: string = params.id;

      this.loader.startLoad(false);

      this.tripService.getLightById(tripId)
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(trip => {
          this.trip = trip;

          this.dateTimezoneService.timezoneSourceChange(true, trip.timezone);
          this.watchTripPosition(trip);
          this.initTrip();
        }, (error: HttpErrorResponse) => {
          console.log(error);
          this.toastr.error(error.error.message || 'Error while fetching the trip', 'Error');
        });
    });

    this.latLngLiteralSubscription = this.latLngLiteralSubject.pipe(
      debounceTime(1000)
    ).subscribe((latLngLiteral: LatLngLiteral) => {
      this.lat = latLngLiteral.lat;
      this.lng = latLngLiteral.lng;
      this.cdr.detectChanges();
    });

    this.zoomSubscription = this.zoomSubject.pipe(
      debounceTime(1000)
    ).subscribe(zoom => {
      this.zoom = zoom;
      this.cdr.detectChanges();
    });
  }

  ngOnInit() {
  }

  initTrip() {
    const positionsObservable = this.tripManagementService.initTrip(this.trip, true);

    this.loader.startLoadAndCloseWhenComplete(positionsObservable.pipe(
      tap(([origin, destination]) => {
        this.trip.originPosition = origin;
        this.trip.destPosition   = destination;
      }),
      concatMap(() => this.tripManagementService.refreshTripDetails(
        this.trip,
        true,
        false,
        false,
        true,
        false,
        this.showForecastPath,
        null,
        null,
        this.distanceUnit
      )
        .observable),
    ))
      .subscribe(() => {
        if (this.trip && this.trip.latestPing) {
          this.lat = this.trip.latestPing.latitude;
          this.lng = this.trip.latestPing.longitude;
        }
        if (this.trip) {
          this.trip.timelineParamsInitialized = true;
          setTimeout(() => this.centerView(), 1000);
        }
        this.cdr.detectChanges();
      });
  }

  watchTripPosition(trip: Trip) {
    this.positionSubscription = this.tripManagementService.getTripPositionObservable(this.trip)
      .subscribe(ping => {
        trip.latestPing = ping;
        this.centerView();
        this.cdr.detectChanges();
      });
  }

  resetSubscriptions() {
    if (this.positionSubscription) {
      this.positionSubscription.unsubscribe();
    }
  }

  forceRefreshTripDetails(trip: Trip) {
    if (!trip) {
      return;
    }
    this.refreshing = true;
    this.loader.startLoadAndCloseWhenComplete(
      this.tripManagementService.refreshTripDetails(
        trip,
        true,
        false,
        true,
        false,
        false,
        this.showForecastPath,
        null,
        null,
        this.distanceUnit
      )
      .observable
    )
    .subscribe(() => {
      this.refreshing = false;
      this.cdr.detectChanges();
    });
  }

  changeShowForecastPath() {
    if (this.showForecastPath && this.trip) {
      this.loader.startLoadAndCloseWhenComplete(
        this.tripManagementService.refreshTripForecastPath(this.trip, true, this.distanceUnit, true)
      ).subscribe(() => {
        this.forecastPathReady = true;
        this.cdr.detectChanges();
      });
    }
  }

  distanceUnitChanged(distanceUnit: DistanceUnit) {
    if (this.tripManagementService.metricsDistance.has(this.trip.tripId) &&
      this.tripManagementService.imperialDistance.has(this.trip.tripId)) {
      let distance
      if (distanceUnit === DistanceUnit.MILES) {
        distance = this.tripManagementService.imperialDistance.get(this.trip.tripId);
      } else {
        distance = this.tripManagementService.metricsDistance.get(this.trip.tripId);
      }
      this.trip.distanceToArrival = distance;
      this.cdr.detectChanges();
    }
  }

  onMapReady(map: Map) {
    this.map = map;
  }

  centerView() {
    this.mapManagementService.centerView(this.trip, this.map, true);
  }

  ngOnDestroy() {
    this.resetSubscriptions();
  }
}
